import { useEffect } from 'react';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import FloatingButtons from '../../../components/feature/FloatingButtons';
import Breadcrumb from '../../../components/feature/Breadcrumb';
import FAQSchema from '../../../components/feature/FAQSchema';
import InternalLinks from '../../../components/feature/InternalLinks';

const BariyerTurnikeSistemlerKullanimAlanlari = () => {
  useEffect(() => {
    document.title = 'Bariyer ve Turnike Sistemleri Kullanım Alanları | Geçiş Kontrol Rehberi | FiberNet Teknoloji';

    const updateMetaTag = (name: string, content: string, property?: boolean) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    updateMetaTag('description', 'Bariyer ve turnike sistemleri kullanım alanları, plaka tanıma teknolojisi ve seçim kriterleri hakkında detaylı rehber. Fabrika ve işyeri güvenlik çözümleri.');
    updateMetaTag('keywords', 'bariyer sistemi, turnike sistemi, plaka tanıma, geçiş kontrol, fabrika güvenlik, araç bariyeri, yaya turnikesi');
    updateMetaTag('author', 'FiberNet Teknoloji');
    updateMetaTag('robots', 'index, follow');
    updateMetaTag('og:title', 'Bariyer ve Turnike Sistemleri Kullanım Alanları | FiberNet Teknoloji', true);
    updateMetaTag('og:description', 'Bariyer ve turnike sistemleri kullanım alanları, plaka tanıma teknolojisi ve seçim kriterleri hakkında detaylı rehber.', true);
    updateMetaTag('og:image', 'https://public.readdy.ai/ai/img_res/d5fc8f5d10e92d242823fffa04912093.jpg', true);

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = 'https://fibernetteknoloji.com/blog/bariyer-turnike-sistemleri-kullanim-alanlari';
  }, []);

  const breadcrumbItems = [
    { label: 'Anasayfa', href: '/' },
    { label: 'Blog', href: '/blog' },
    { label: 'Bariyer ve Turnike Sistemleri Kullanım Alanları' }
  ];

  const faqData = [
    {
      question: "Araç bariyer sistemi ile turnike sistemi arasındaki fark nedir?",
      answer: "Araç bariyer sistemleri motorlu taşıtların geçişini kontrol ederken, turnike sistemleri yaya geçişini kontrol eder. Bariyer sistemler genellikle otopark ve araç girişlerinde, turnikeler ise fabrika girişleri ve güvenlik noktalarında kullanılır."
    },
    {
      question: "Plaka tanıma sistemi nasıl çalışır?",
      answer: "Plaka tanıma sistemi kamera ve yapay zeka teknolojisi kullanarak araç plakalarını otomatik okur. Sistem, önceden tanımlanmış plakaları tespit ettiğinde bariyeri otomatik olarak açar ve geçiş kayıtlarını tutar."
    },
    {
      question: "Turnike sistemleri hangi kartlarla çalışır?",
      answer: "Modern turnike sistemleri RFID kartlar, akıllı kartlar, manyetik kartlar ve mobil uygulamalarla çalışabilir. Ayrıca parmak izi, yüz tanıma gibi biyometrik sistemlerle de entegre edilebilir."
    },
    {
      question: "Acil durumlarda bariyer ve turnikeler nasıl çalışır?",
      answer: "Acil durumlarda (yangın alarmı, elektrik kesintisi) sistemler otomatik olarak serbest geçiş konumuna gelir. Bu güvenlik özelliği yangın yönetmeliklerinin gereksinimi olup, manuel açma seçenekleri de bulunur."
    }
  ];

  return (
    <>
      <Navbar />
      
      <main className="min-h-screen bg-white">
        {/* Breadcrumb */}
        <section className="bg-gray-50 py-4">
          <div className="container mx-auto px-4">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </section>

        {/* Hero Section */}
        <section className="py-12 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-block bg-orange-500 text-white px-3 py-1 rounded-full text-sm font-medium mb-4">
                Güvenlik Sistemleri
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Bariyer ve Turnike Sistemleri Kullanım Alanları
              </h1>
              <p className="text-xl text-blue-100 mb-8">
                Modern bariyer ve turnike sistemlerinin kullanım alanları, plaka tanıma teknolojisi ve seçim kriterleri hakkında kapsamlı rehber
              </p>
              <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-blue-100">
                <span>📅 15 Ocak 2025</span>
                <span>⏱️ 9 dakika okuma</span>
                <span>🚧 Güvenlik Sistemleri</span>
              </div>
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              
              {/* Featured Image */}
              <div className="mb-12">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20automatic%20vehicle%20barrier%20gate%20system%20with%20license%20plate%20recognition%20camera%20at%20factory%20entrance%2C%20modern%20access%20control%20equipment%20with%20turnstile%20gates%20for%20pedestrians%2C%20clean%20industrial%20security%20installation&width=800&height=450&seq=barrier-turnstile-entrance&orientation=landscape"
                  alt="Bariyer ve Turnike Sistemleri"
                  className="w-full h-96 object-cover rounded-lg shadow-lg"
                />
              </div>

              <div className="prose max-w-none">
                
                {/* Giriş */}
                <div className="mb-12">
                  <p className="text-lg text-gray-700 leading-8 mb-6">
                    Bariyer ve turnike sistemleri, modern güvenlik altyapısının vazgeçilmez bileşenleridir. Bu sistemler, kontrollü erişim sağlayarak hem güvenliği artırır hem de geçiş kayıtlarını tutar. Fabrika, ofis, otopark ve diğer güvenlik gerektiren alanlarda yaygın olarak kullanılmaktadır. Doğru sistem seçimi, kullanım amacına ve alan özelliklerine göre yapılmalıdır.
                  </p>
                </div>

                {/* Sistem Türleri */}
                <div className="mb-12">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">Bariyer ve Turnike Sistem Türleri</h2>
                  
                  <div className="grid md:grid-cols-2 gap-8 mb-8">
                    <div className="bg-blue-50 p-6 rounded-lg">
                      <h3 className="text-xl font-semibold text-blue-800 mb-4">🚗 Araç Bariyer Sistemleri</h3>
                      <ul className="space-y-3 text-gray-700">
                        <li><strong>Otomatik Bariyer:</strong> Uzaktan kumanda veya sensörle çalışır</li>
                        <li><strong>Manuel Bariyer:</strong> Güvenlik görevlisi kontrolünde</li>
                        <li><strong>Hidrolik Bariyer:</strong> Ağır hizmet tipi, yüksek geçiş sıklığı</li>
                        <li><strong>Elektrikli Bariyer:</strong> Orta seviye kullanım için</li>
                      </ul>
                    </div>
                    
                    <div className="bg-orange-50 p-6 rounded-lg">
                      <h3 className="text-xl font-semibold text-orange-800 mb-4">🚶 Yaya Turnike Sistemleri</h3>
                      <ul className="space-y-3 text-gray-700">
                        <li><strong>Tripod Turnike:</strong> Üç kollu, kompakt tasarım</li>
                        <li><strong>Full Height Turnike:</strong> Tam boy, maksimum güvenlik</li>
                        <li><strong>Swing Gate:</strong> Engelli erişimi uyumlu</li>
                        <li><strong>Speed Gate:</strong> Hızlı geçiş, modern tasarım</li>
                      </ul>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3">Seçim Kriterleri</h4>
                    <div className="grid md:grid-cols-3 gap-4">
                      <ul className="space-y-2 text-gray-700">
                        <li>✓ <strong>Geçiş Sıklığı:</strong> Günlük kullanım sayısı</li>
                        <li>✓ <strong>Güvenlik Seviyesi:</strong> Düşük, orta, yüksek</li>
                      </ul>
                      <ul className="space-y-2 text-gray-700">
                        <li>✓ <strong>Çevre Koşulları:</strong> İç mekan, dış mekan</li>
                        <li>✓ <strong>Entegrasyon:</strong> Diğer sistemlerle uyumluluk</li>
                      </ul>
                      <ul className="space-y-2 text-gray-700">
                        <li>✓ <strong>Bütçe:</strong> İlk yatırım ve işletme maliyeti</li>
                        <li>✓ <strong>Bakım:</strong> Bakım gereksinimleri</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Kullanım Alanları */}
                <div className="mb-12">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">Kullanım Alanları ve Uygulamalar</h2>
                  
                  <div className="mb-8">
                    <img 
                      src="https://readdy.ai/api/search-image?query=industrial%20factory%20complex%20with%20multiple%20security%20checkpoints%20featuring%20vehicle%20barriers%20and%20pedestrian%20turnstiles%2C%20modern%20access%20control%20systems%20at%20various%20entry%20points%2C%20professional%20security%20infrastructure&width=800&height=400&seq=security-checkpoints&orientation=landscape"
                      alt="Güvenlik Kontrol Noktaları"
                      className="w-full h-64 object-cover rounded-lg shadow-lg mb-6"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-8 mb-8">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-4">🏭 Sanayi ve Fabrika Uygulamaları</h3>
                      <div className="bg-teal-50 p-4 rounded-lg">
                        <ul className="space-y-3 text-gray-700">
                          <li><strong>Ana Giriş Kontrolü:</strong> Araç ve yaya geçiş kontrolü</li>
                          <li><strong>İç Alan Ayırımı:</strong> Üretim alanları arası geçiş</li>
                          <li><strong>Depo Girişleri:</strong> Hammadde ve ürün depo erişimi</li>
                          <li><strong>Personel Geçişi:</strong> Vardiya değişimi kontrolü</li>
                          <li><strong>Ziyaretçi Yönetimi:</strong> Geçici erişim kontrolü</li>
                        </ul>
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-4">🏢 Ticari ve Kurumsal Alanlar</h3>
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <ul className="space-y-3 text-gray-700">
                          <li><strong>Ofis Binaları:</strong> Lobby ve asansör erişimi</li>
                          <li><strong>AVM'ler:</strong> Personel ve hizmet girişleri</li>
                          <li><strong>Hastaneler:</strong> Kısıtlı alan erişim kontrolü</li>
                          <li><strong>Okullar:</strong> Kampüs güvenliği ve kontrol</li>
                          <li><strong>Havaalanları:</strong> Güvenlik bölgeleri geçişi</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="bg-orange-50 p-6 rounded-lg">
                    <h4 className="font-semibold text-orange-800 mb-3">Özel Kullanım Alanları</h4>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <h5 className="font-semibold text-orange-700 mb-2">Otopark Sistemleri</h5>
                        <ul className="space-y-1 text-gray-700 text-sm">
                          <li>• Ücretli otopark girişi</li>
                          <li>• Aboneli araç tanıma</li>
                          <li>• Kapasite yönetimi</li>
                          <li>• Ödeme sistemi entegrasyonu</li>
                        </ul>
                      </div>
                      <div>
                        <h5 className="font-semibold text-orange-700 mb-2">Spor Tesisleri</h5>
                        <ul className="space-y-1 text-gray-700 text-sm">
                          <li>• Üyelik kontrolü</li>
                          <li>• Saha erişim yönetimi</li>
                          <li>• Zaman bazlı geçiş</li>
                          <li>• Kapasite sınırlaması</li>
                        </ul>
                      </div>
                      <div>
                        <h5 className="font-semibold text-orange-700 mb-2">Kamu Binaları</h5>
                        <ul className="space-y-1 text-gray-700 text-sm">
                          <li>• Güvenlik taraması öncesi</li>
                          <li>• Ziyaretçi kayıt sistemi</li>
                          <li>• Randevu bazlı erişim</li>
                          <li>• Acil durum yönetimi</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Plaka Tanıma Teknolojisi */}
                <div className="mb-12">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">Plaka Tanıma Teknolojisi</h2>
                  
                  <div className="mb-8">
                    <img 
                      src="https://readdy.ai/api/search-image?query=automatic%20license%20plate%20recognition%20ANPR%20camera%20system%20reading%20vehicle%20number%20plate%20at%20barrier%20gate%20entrance%2C%20modern%20AI%20powered%20security%20technology%20with%20clear%20display%20screen%20showing%20detected%20plate&width=800&height=400&seq=plate-recognition&orientation=landscape"
                      alt="Plaka Tanıma Sistemi"
                      className="w-full h-64 object-cover rounded-lg shadow-lg mb-6"
                    />
                  </div>

                  <div className="bg-gradient-to-r from-blue-50 to-teal-50 p-6 rounded-lg mb-6">
                    <h3 className="text-xl font-semibold text-blue-800 mb-4">Plaka Tanıma Sistemi Özellikleri</h3>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold text-blue-700 mb-3">Teknik Özellikler</h4>
                        <ul className="space-y-2 text-gray-700 text-sm">
                          <li>• <strong>Yüksek Çözünürlük:</strong> 2MP+ kamera teknolojisi</li>
                          <li>• <strong>Gece Görüş:</strong> IR LED aydınlatma</li>
                          <li>• <strong>Hız Toleransı:</strong> 5-80 km/h arası okuma</li>
                          <li>• <strong>Mesafe:</strong> 3-8 metre arası algılama</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-blue-700 mb-3">Yazılım Özellikleri</h4>
                        <ul className="space-y-2 text-gray-700 text-sm">
                          <li>• <strong>AI Algoritması:</strong> Yapay zeka destekli okuma</li>
                          <li>• <strong>Veri Tabanı:</strong> Yerel ve bulut depolama</li>
                          <li>• <strong>Raporlama:</strong> Detaylı geçiş raporları</li>
                          <li>• <strong>Entegrasyon:</strong> ERP sistemleri bağlantısı</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-3 gap-6 mb-8">
                    <div className="text-center p-6 bg-white border-2 border-blue-200 rounded-lg">
                      <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
                        <i className="ri-camera-lens-line text-2xl text-blue-600"></i>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Otomatik Okuma</h4>
                      <p className="text-sm text-gray-600">Araç yaklaştığında otomatik plaka okuma ve tanıma</p>
                    </div>
                    <div className="text-center p-6 bg-white border-2 border-teal-200 rounded-lg">
                      <div className="w-16 h-16 mx-auto mb-4 bg-teal-100 rounded-full flex items-center justify-center">
                        <i className="ri-database-2-line text-2xl text-teal-600"></i>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Veri Saklama</h4>
                      <p className="text-sm text-gray-600">Geçiş kayıtları, fotoğraf ve zaman damgası</p>
                    </div>
                    <div className="text-center p-6 bg-white border-2 border-orange-200 rounded-lg">
                      <div className="w-16 h-16 mx-auto mb-4 bg-orange-100 rounded-full flex items-center justify-center">
                        <i className="ri-notification-line text-2xl text-orange-600"></i>
                      </div>
                      <h4 className="font-semibold text-gray-900 mb-2">Anlık Bildirim</h4>
                      <p className="text-sm text-gray-600">Yetkisiz araç tespitinde anında uyarı</p>
                    </div>
                  </div>

                  <p className="text-gray-700 leading-8">
                    Modern plaka tanıma sistemleri, yapay zeka ve makine öğrenmesi teknolojilerini kullanarak %99+ doğrulukla plaka okuyabilir. Bu sistemler, hem güvenliği artırır hem de operasyonel verimliliği önemli ölçüde artırır.
                  </p>
                </div>

                {/* Seçim Rehberi */}
                <div className="mb-12">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">Bariyer ve Turnike Seçim Rehberi</h2>
                  
                  <div className="bg-gray-50 p-6 rounded-lg mb-8">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Sistem Seçim Adımları</h3>
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-3">1. İhtiyaç Analizi</h4>
                        <ul className="space-y-2 text-gray-700 text-sm">
                          <li>• Günlük geçiş sayısının belirlenmesi</li>
                          <li>• Güvenlik seviyesi gereksinimlerinin tespiti</li>
                          <li>• Kullanıcı profillerinin analizi</li>
                          <li>• Engelli erişimi gereksinimlerinin değerlendirilmesi</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-800 mb-3">2. Teknik Gereksinimler</h4>
                        <ul className="space-y-2 text-gray-700 text-sm">
                          <li>• Elektrik altyapısının kontrolü</li>
                          <li>• Ağ bağlantısı imkanlarının tespiti</li>
                          <li>• Montaj alanının ölçümü</li>
                          <li>• Çevresel koşulların değerlendirilmesi</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="bg-blue-50 p-6 rounded-lg">
                      <h4 className="font-semibold text-blue-800 mb-3">💰 Bütçe Planlaması</h4>
                      <ul className="space-y-2 text-gray-700 text-sm">
                        <li>• Sistem maliyeti</li>
                        <li>• Kurulum ücreti</li>
                        <li>• Yıllık bakım maliyeti</li>
                        <li>• Yedek parça bütçesi</li>
                        <li>• Eğitim maliyetleri</li>
                      </ul>
                    </div>
                    <div className="bg-teal-50 p-6 rounded-lg">
                      <h4 className="font-semibold text-teal-800 mb-3">⚡ Performans Kriterleri</h4>
                      <ul className="space-y-2 text-gray-700 text-sm">
                        <li>• Geçiş hızı (kişi/dakika)</li>
                        <li>• Sistem güvenilirliği (%)</li>
                        <li>• Hata toleransı</li>
                        <li>• Bakım sıklığı</li>
                        <li>• Garanti süresi</li>
                      </ul>
                    </div>
                    <div className="bg-orange-50 p-6 rounded-lg">
                      <h4 className="font-semibold text-orange-800 mb-3">🔧 Kurulum Faktörleri</h4>
                      <ul className="space-y-2 text-gray-700 text-sm">
                        <li>• Zemin uygunluğu</li>
                        <li>• Hava koşulları dayanımı</li>
                        <li>• Bakım erişimi</li>
                        <li>• Genişleme imkanı</li>
                        <li>• Entegrasyon kolaylığı</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* İlgili Hizmetlerimiz */}
                <div className="mb-12">
                  <div className="bg-gradient-to-r from-blue-50 to-teal-50 p-8 rounded-lg">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">İlgili Hizmetlerimiz</h2>
                    <div className="grid md:grid-cols-2 gap-6">
                      <a 
                        href="/security-systems/barrier-systems" 
                        className="block bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 group"
                      >
                        <div className="flex items-center mb-4">
                          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                            <i className="ri-roadster-line text-xl text-blue-600"></i>
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600">Bariyer Sistemleri</h3>
                        </div>
                        <p className="text-gray-600 text-sm">Otomatik araç bariyer sistemleri, plaka tanıma ve otopark yönetim çözümleri.</p>
                      </a>
                      
                      <a 
                        href="/security-systems/turnstile-systems" 
                        className="block bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 group"
                      >
                        <div className="flex items-center mb-4">
                          <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4">
                            <i className="ri-door-line text-xl text-teal-600"></i>
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900 group-hover:text-teal-600">Turnike Sistemleri</h3>
                        </div>
                        <p className="text-gray-600 text-sm">Yaya geçiş kontrol turnikeleri, kartlı geçiş ve biyometrik entegrasyon.</p>
                      </a>

                      <a 
                        href="/security-systems/access-control-systems" 
                        className="block bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 group"
                      >
                        <div className="flex items-center mb-4">
                          <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4">
                            <i className="ri-key-2-line text-xl text-orange-600"></i>
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900 group-hover:text-orange-600">Geçiş Kontrol Sistemleri</h3>
                        </div>
                        <p className="text-gray-600 text-sm">Kapsamlı erişim kontrolü, kartlı geçiş ve biyometrik güvenlik çözümleri.</p>
                      </a>

                      <a 
                        href="/services/osb-access-control-system" 
                        className="block bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 group"
                      >
                        <div className="flex items-center mb-4">
                          <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mr-4">
                            <i className="ri-building-2-line text-xl text-red-600"></i>
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900 group-hover:text-red-600">OSB Geçiş Kontrol Sistemi</h3>
                        </div>
                        <p className="text-gray-600 text-sm">Organize sanayi bölgeleri için özel geçiş kontrol ve güvenlik sistemleri.</p>
                      </a>
                    </div>
                  </div>
                </div>

                {/* FAQ Section */}
                <div className="mb-12">
                  <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Sıkça Sorulan Sorular</h2>
                  <FAQSchema faqs={faqData} />
                </div>

                {/* CTA Section */}
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-8 rounded-lg text-center">
                  <h2 className="text-2xl font-bold mb-4">Profesyonel Bariyer ve Turnike Sistemi Kurulumu</h2>
                  <p className="text-blue-100 mb-6">
                    İşyeriniz için en uygun geçiş kontrol çözümlerini keşfedin. 
                    Uzman ekibimizden ücretsiz keşif ve teklif alın.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <a 
                      href="tel:+905537165561" 
                      className="bg-white text-blue-600 px-6 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-all duration-300 whitespace-nowrap"
                    >
                      📞 Hemen Ara: +90 553 716 55 61
                    </a>
                    <a 
                      href="/contact" 
                      className="bg-orange-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-600 transition-all duration-300 whitespace-nowrap"
                    >
                      💬 Online Teklif Al
                    </a>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </section>

        <InternalLinks />
      </main>

      <Footer />
      <FloatingButtons />
    </>
  );
};

export default BariyerTurnikeSistemlerKullanimAlanlari;
